import ContextReminderContainer from '@app/components/ContextReminder'
import { CoverPhoto, CoverPhotoOverlay } from '@app/components/CoverPhoto'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import { externalActionCreators as dueDiligenceActions } from '@app/components/DueDiligenceItemEdit/modules/dueDiligenceItemEdit'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import { INavTabItemProps } from '@app/components/NavTabs/NavTabItem'
import { DevelopmentSiteStatus, ImageSize } from '@app/models/enums'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import { formatAddressSingleLine, humanize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import {
  actionCreators,
  IDevelopmentSiteViewState,
} from '../modules/developmentSiteView'
import DevelopmentSiteActivity from './DevelopmentSiteActivity'
import DevelopmentSiteCityProfile from './DevelopmentSiteCityProfile'
import DevelopmentSiteCriticalDates from './DevelopmentSiteCriticalDates'
import DevelopmentSiteFiles from './DevelopmentSiteFiles'
import DevelopmentSitePipeline from './DevelopmentSitePipeline'
import DevelopmentSiteTeam from './DevelopmentSiteTeam'
import './DevelopmentSiteViewPage.scss'

export type DevelopmentSiteViewPageProps = IDevelopmentSiteViewState &
  typeof actionCreators &
  typeof dueDiligenceActions &
  RouteComponentProps<{ clientSlug: string; siteSlug: string }> &
  ICurrentUserState

export class DevelopmentSiteViewPage extends React.PureComponent<DevelopmentSiteViewPageProps> {
  constructor(props: DevelopmentSiteViewPageProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.loadDevelopmentSite(
      this.props.match.params.clientSlug,
      this.props.match.params.siteSlug,
    )
  }

  public componentDidUpdate(prevProps: DevelopmentSiteViewPageProps) {
    const prevParams = prevProps.match.params
    const currParams = this.props.match.params

    if (
      currParams.clientSlug !== prevParams.clientSlug ||
      currParams.siteSlug !== prevParams.siteSlug
    ) {
      this.props.loadDevelopmentSite(currParams.clientSlug, currParams.siteSlug)
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (this.props.isLoading) {
      return (
        <DocumentTitle title='Development Site :: Loading...'>
          <div className='my-3 text-center'>
            Retrieving development site information. Please wait...
          </div>
        </DocumentTitle>
      )
    } else if (!this.props.site.id) {
      return (
        <DocumentTitle title='Development Site :: Error'>
          <div className='my-3 text-center'>
            Unable to load development site
          </div>
        </DocumentTitle>
      )
    }

    return (
      <DocumentTitle
        title={`${this.props.site.client.name} :: ${formatAddressSingleLine(
          this.props.site.address,
        )}`}
      >
        <Container className='my-3'>
          {this._renderHeader()}
          {this._renderTabs()}
          <Switch>
            <Route
              path={`${this.props.match.path}/cityProfile`}
              render={() => <DevelopmentSiteCityProfile {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/team`}
              render={() => <DevelopmentSiteTeam {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/pipeline`}
              render={() => <DevelopmentSitePipeline {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/criticalDates`}
              render={() => <DevelopmentSiteCriticalDates {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/files`}
              render={() => <DevelopmentSiteFiles {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/reminders`}
              render={() => (
                <ContextReminderContainer
                  contextId={this.props.site.id}
                  {...this.props}
                />
              )}
            />
            <Route
              path={this.props.match.path}
              render={() => <DevelopmentSiteActivity {...this.props} />}
            />
          </Switch>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={
              this.props.site.name ||
              formatAddressSingleLine(this.props.site.address)
            }
            onConfirm={this._onConfirmDelete}
            onToggleModal={this._onToggleModal}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _getCoverPhotoUrl(): string {
    if (this.props.site.coverPhoto) {
      return this.props.site.coverPhoto.getImageUrl(ImageSize.original)
    } else {
      return `/images/default-property.png`
    }
  }

  private _onConfirmDelete() {
    this.props.deleteDevelopmentSite(
      this.props.site.client.clientId,
      this.props.site.id,
    )
  }

  private _onCreateProperty() {
    if (
      this.props.site.status !== DevelopmentSiteStatus.Potential &&
      !this.props.site.propertyId
    ) {
      this.props.history.push(
        `/properties/property/add?site=${this.props.site.id}`,
      )
    }
  }

  private _onDelete() {
    this.props.toggleConfirmDelete(true)
  }

  private _onEdit() {
    this.props.history.push(
      `/development/client/${this.props.match.params.clientSlug}/site/${this.props.match.params.siteSlug}/edit`,
    )
  }

  private _onGoToProperty() {
    if (this.props.site.propertyId) {
      this.props.history.push(
        `/properties/property/${this.props.site.propertyId}`,
      )
    }
  }

  private _onToggleModal() {
    this.props.toggleConfirmDelete(false)
  }

  private _renderHeader() {
    return (
      <Card>
        <CardHeader
          className={`development-site-header ${this.props.site.status.toLowerCase()} d-flex justify-content-between`}
        >
          <div>
            <span className='text-uppercase'>
              {humanize(this.props.site.status)}
            </span>
          </div>
          <div>
            {(this.props.permissions.canEdit ||
              this.props.permissions.canDelete) && (
              <UncontrolledDropdown className='d-inline-block'>
                <DropdownToggle tag='a' className='px-1 text-center'>
                  <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
                </DropdownToggle>
                <DropdownMenu right>
                  {this.props.site.propertyId && (
                    <DropdownItem onClick={this._onGoToProperty}>
                      Go to property
                    </DropdownItem>
                  )}
                  {this.props.permissions.canEdit &&
                    this.props.site.status !==
                      DevelopmentSiteStatus.Potential &&
                    !this.props.site.propertyId && (
                      <DropdownItem onClick={this._onCreateProperty}>
                        Create Property
                      </DropdownItem>
                    )}
                  {this.props.permissions.canEdit && (
                    <DropdownItem onClick={this._onEdit}>Edit</DropdownItem>
                  )}
                  {this.props.permissions.canDelete && (
                    <DropdownItem onClick={this._onDelete}>Delete</DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        </CardHeader>
        <CoverPhoto photoUrl={this._getCoverPhotoUrl()}>
          <CoverPhotoOverlay>
            <div className='font-weight-bold'>{this.props.site.name}</div>
            <div>{formatAddressSingleLine(this.props.site.address)}</div>
          </CoverPhotoOverlay>
        </CoverPhoto>
      </Card>
    )
  }

  private _renderTabs() {
    return (
      <NavTabs className='my-3'>
        <NavTabItem
          exact
          iconName='chart-line'
          label='Activity'
          to={this.props.match.url}
        />
        <NavTabItem
          iconName='industry-alt'
          label='City Profile'
          to={`${this.props.match.url}/cityProfile`}
        />
        <NavTabItem
          iconName='users'
          label='Team'
          to={`${this.props.match.url}/team`}
        />
        <NavTabItem
          iconName='clipboard-list'
          label='Pipeline'
          to={`${this.props.match.url}/pipeline`}
        />
        {this.props.site.status !== DevelopmentSiteStatus.Potential
          ? ((
              <NavTabItem
                iconName='alarm-clock'
                label='Critical Dates'
                to={`${this.props.match.url}/criticalDates`}
              />
            ) as React.ReactElement<INavTabItemProps>)
          : null}
        <NavTabItem
          badgeCount={
            this.props.site.attachments.size + this.props.site.photos.size
          }
          iconName='paperclip'
          label='Files'
          to={`${this.props.match.url}/files`}
        />
        <NavTabItem
          iconName='bell'
          label='Reminders'
          to={`${this.props.match.url}/reminders`}
        />
      </NavTabs>
    )
  }
}
